import React, { useState, useRef, useEffect } from 'react';
import { useContent } from '../../context/ContentContext';
import { PwaOnboardingSlide } from '../../types';
import { resolveMediaUrl } from '../../utils/api';
import { PwaOnboarding } from '../public/PwaOnboarding';
import {
  Smartphone,
  Upload,
  Trash2,
  ArrowUp,
  ArrowDown,
  Save,
  Plus,
  Loader2,
  Check,
  Eye,
  EyeOff,
  Sparkles,
  RotateCcw,
  Edit2,
  X,
  Layers,
  AlertCircle,
  HelpCircle,
} from 'lucide-react';

const DEFAULT_ONBOARDING_SLIDES: PwaOnboardingSlide[] = [
  {
    id: 'slide-1',
    order: 1,
    image: 'https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?auto=format&fit=crop&w=1200&q=80',
    title: 'Nikmati Kopi Spesialti Asli Dumai',
    description: 'Biji kopi pilihan nusantara yang disangrai dengan presisi untuk menghadirkan aroma dan cita rasa terbaik di setiap cangkir.',
    enabled: true,
  },
  {
    id: 'slide-2',
    order: 2,
    image: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=1200&q=80',
    title: 'Pesan Mudah & Kumpulkan Poin',
    description: 'Pesan langsung dari mejamu, kumpulkan loyalty point, dan nikmati berbagai reward eksklusif member LetonKu.',
    enabled: true,
  },
  {
    id: 'slide-3',
    order: 3,
    image: 'https://images.unsplash.com/photo-1514432324607-a09d9b4aefdd?auto=format&fit=crop&w=1200&q=80',
    title: 'Kunjungi Outlet & Nikmati Suasananya',
    description: 'Temukan Chapter 5 Sudirman, Chapter 6 Kelakap 7, atau layanan Let’GO Mobile Coffee di kotamu.',
    enabled: true,
  },
];

export const PwaOnboardingManager: React.FC = () => {
  const { data, updateData, uploadImage, showToast } = useContent();

  const [slides, setSlides] = useState<PwaOnboardingSlide[]>(() => {
    if (Array.isArray(data.pwaOnboardingSlides) && data.pwaOnboardingSlides.length > 0) {
      return data.pwaOnboardingSlides;
    }
    return DEFAULT_ONBOARDING_SLIDES;
  });

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Modal / Form state for Add / Edit
  const [editingSlide, setEditingSlide] = useState<PwaOnboardingSlide | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [formTitle, setFormTitle] = useState<string>('');
  const [formDescription, setFormDescription] = useState<string>('');
  const [formImage, setFormImage] = useState<string>('');
  const [formEnabled, setFormEnabled] = useState<boolean>(true);
  const [formOrder, setFormOrder] = useState<number>(1);
  const [showLivePreviewModal, setShowLivePreviewModal] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync when context data changes
  useEffect(() => {
    if (Array.isArray(data.pwaOnboardingSlides) && data.pwaOnboardingSlides.length > 0) {
      setSlides(data.pwaOnboardingSlides);
    }
  }, [data.pwaOnboardingSlides]);

  const handleOpenAddModal = () => {
    const nextOrder = slides.length > 0 ? Math.max(...slides.map((s) => s.order || 0)) + 1 : 1;
    setEditingSlide(null);
    setFormTitle('');
    setFormDescription('');
    setFormImage('');
    setFormEnabled(true);
    setFormOrder(nextOrder);
    setUploadError(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (slide: PwaOnboardingSlide) => {
    setEditingSlide(slide);
    setFormTitle(slide.title || '');
    setFormDescription(slide.description || '');
    setFormImage(slide.image || '');
    setFormEnabled(slide.enabled !== false);
    setFormOrder(slide.order || 1);
    setUploadError(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingSlide(null);
    setUploadError(null);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setUploadError('Format file tidak didukung. Harap upload file gambar (JPG/PNG/WEBP).');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setUploadError('Ukuran file terlalu besar. Maksimal 10MB.');
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      const uploadedUrl = await uploadImage(file, 'pwa_onboarding');
      if (uploadedUrl) {
        setFormImage(uploadedUrl);
        showToast('Gambar slide berhasil diupload!', 'success');
      } else {
        setUploadError('Gagal mengupload gambar. Silakan coba lagi.');
      }
    } catch (err: any) {
      setUploadError('Terjadi kesalahan saat upload gambar: ' + (err?.message || 'Error'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleSaveModalForm = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formTitle.trim()) {
      setUploadError('Judul slide wajib diisi.');
      return;
    }

    if (editingSlide) {
      // Edit existing
      setSlides((prev) =>
        prev.map((s) =>
          s.id === editingSlide.id
            ? {
                ...s,
                title: formTitle.trim(),
                description: formDescription.trim(),
                image: formImage.trim(),
                enabled: formEnabled,
                order: Number(formOrder) || 1,
              }
            : s
        )
      );
      showToast('Slide onboarding diperbarui!', 'info');
    } else {
      // Add new slide
      const newSlide: PwaOnboardingSlide = {
        id: `slide-${Date.now()}`,
        title: formTitle.trim(),
        description: formDescription.trim(),
        image: formImage.trim(),
        enabled: formEnabled,
        order: Number(formOrder) || 1,
      };
      setSlides((prev) => [...prev, newSlide]);
      showToast('Slide onboarding baru ditambahkan!', 'success');
    }

    handleCloseModal();
  };

  const handleDeleteSlide = (slideId: string) => {
    if (slides.length <= 1) {
      showToast('Minimal harus ada 1 slide onboarding.', 'error');
      return;
    }
    if (window.confirm('Hapus slide onboarding ini?')) {
      setSlides((prev) => prev.filter((s) => s.id !== slideId));
      showToast('Slide dihapus dari daftar.', 'info');
    }
  };

  const handleToggleEnable = (slideId: string) => {
    setSlides((prev) =>
      prev.map((s) => (s.id === slideId ? { ...s, enabled: !s.enabled } : s))
    );
  };

  const handleMoveOrder = (index: number, direction: 'up' | 'down') => {
    const newSlides = [...slides];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newSlides.length) return;

    const temp = newSlides[index];
    newSlides[index] = newSlides[targetIndex];
    newSlides[targetIndex] = temp;

    // Normalize order
    const updated = newSlides.map((s, idx) => ({ ...s, order: idx + 1 }));
    setSlides(updated);
  };

  const handleResetToDefaults = () => {
    if (window.confirm('Kembalikan ke 3 slide onboarding default?')) {
      setSlides(DEFAULT_ONBOARDING_SLIDES);
      showToast('Daftar slide dikembalikan ke default.', 'info');
    }
  };

  const handleSaveAllToDatabase = async () => {
    setIsSaving(true);
    try {
      const sorted = [...slides].map((s, idx) => ({ ...s, order: idx + 1 }));
      const success = await updateData((prev) => ({
        ...prev,
        pwaOnboardingSlides: sorted,
      }));

      if (success) {
        showToast('Konfigurasi PWA Onboarding berhasil disimpan ke cloud!', 'success');
      } else {
        showToast('Gagal menyimpan konfigurasi onboarding.', 'error');
      }
    } catch (err: any) {
      showToast('Terjadi kesalahan: ' + (err?.message || 'Error'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-8 max-w-5xl mx-auto pb-12">
      {/* ---------------------------------------------------- */}
      {/* HEADER & ACTIONS                                     */}
      {/* ---------------------------------------------------- */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-[#E0F2FE] shadow-sm">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-sky-50 text-[#0284C7] border border-[#E0F2FE]">
              <Smartphone className="w-5 h-5" />
            </div>
            <h2 className="font-display font-black text-xl text-[#172033] tracking-wide uppercase">
              PWA Onboarding Slider
            </h2>
          </div>
          <p className="text-xs text-[#64748B] mt-1.5 leading-relaxed max-w-2xl">
            Atur konten layar onboarding fullscreen yang muncul <strong>hanya 1 kali</strong> saat customer pertama kali membuka aplikasi PWA LetonKu setelah diinstall.
          </p>
        </div>

        <div className="flex items-center gap-2.5 flex-wrap">
          <button
            type="button"
            onClick={() => setShowLivePreviewModal(true)}
            className="px-4 py-2 rounded-xl bg-[#F0F7FF] hover:bg-[#E0F2FE] text-[#0284C7] text-xs font-bold tracking-wider uppercase transition-all flex items-center gap-1.5 cursor-pointer border border-[#E0F2FE]"
          >
            <Eye className="w-4 h-4" />
            <span>Live Preview HP</span>
          </button>

          <button
            type="button"
            onClick={handleOpenAddModal}
            className="px-4 py-2 rounded-xl bg-white hover:bg-slate-50 text-[#172033] border border-slate-300 text-xs font-bold tracking-wider uppercase transition-all flex items-center gap-1.5 cursor-pointer shadow-sm"
          >
            <Plus className="w-4 h-4 text-[#0284C7]" />
            <span>Tambah Slide</span>
          </button>

          <button
            type="button"
            disabled={isSaving}
            onClick={handleSaveAllToDatabase}
            className="px-5 py-2 rounded-xl bg-[#0284C7] hover:bg-[#0369A1] text-white text-xs font-bold tracking-wider uppercase transition-all flex items-center gap-2 shadow-sm cursor-pointer disabled:opacity-50"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Menyimpan...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Simpan Perubahan</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Info Callout */}
      <div className="p-4 rounded-xl bg-sky-50/70 border border-sky-200/70 text-xs text-sky-900 flex items-start gap-3">
        <Sparkles className="w-4 h-4 text-[#0284C7] shrink-0 mt-0.5" />
        <div className="leading-relaxed">
          <strong className="font-semibold block mb-0.5">Aturan Onboarding PWA LetonKu:</strong>
          Customer yang mengakses melalui browser reguler langsung masuk ke homepage tanpa onboarding. Layar ini hanya aktif saat PWA dibuka dari Home Screen perangkat Android atau iOS (standalone mode) dan otomatis tersimpan selesai saat ditekan "Lewati" atau "Mulai".
        </div>
      </div>

      {/* ---------------------------------------------------- */}
      {/* SLIDES LIST                                          */}
      {/* ---------------------------------------------------- */}
      <div className="space-y-4">
        <div className="flex items-center justify-between px-1">
          <span className="text-xs font-mono font-bold uppercase tracking-wider text-[#64748B]">
            Daftar Slide ({slides.length} Slide Aktif)
          </span>
          <button
            type="button"
            onClick={handleResetToDefaults}
            className="text-xs text-slate-500 hover:text-[#0284C7] font-semibold flex items-center gap-1 cursor-pointer transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>Kembalikan Default</span>
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {slides.map((slide, index) => (
            <div
              key={slide.id || index}
              className={`p-5 rounded-2xl bg-white border transition-all ${
                slide.enabled
                  ? 'border-[#E0F2FE] shadow-[0_2px_12px_rgba(0,0,0,0.03)]'
                  : 'border-slate-200 bg-slate-50/60 opacity-60'
              } flex flex-col md:flex-row items-start md:items-center justify-between gap-5`}
            >
              {/* Thumbnail & Info */}
              <div className="flex items-start gap-4 min-w-0 flex-1">
                <div className="w-8 h-8 rounded-lg bg-[#F0F7FF] text-[#0284C7] font-mono font-bold text-xs flex items-center justify-center shrink-0 border border-[#E0F2FE]">
                  #{index + 1}
                </div>

                <div className="w-24 h-16 sm:w-28 sm:h-20 rounded-xl bg-slate-900 overflow-hidden shrink-0 border border-slate-200 shadow-inner">
                  {slide.image ? (
                    <img
                      src={resolveMediaUrl(slide.image)}
                      alt={slide.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-500">
                      <Smartphone className="w-6 h-6" />
                    </div>
                  )}
                </div>

                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display font-bold text-base text-[#172033] truncate">
                      {slide.title || 'Tanpa Judul'}
                    </h3>
                    <span
                      className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold uppercase tracking-wider ${
                        slide.enabled
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-slate-200 text-slate-600'
                      }`}
                    >
                      {slide.enabled ? 'Aktif' : 'Nonaktif'}
                    </span>
                  </div>

                  <p className="text-xs text-[#64748B] line-clamp-2 leading-relaxed">
                    {slide.description || 'Tanpa deskripsi'}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 shrink-0 self-end md:self-center">
                {/* Order Up/Down */}
                <div className="flex items-center bg-[#F8FBFF] border border-[#E0F2FE] rounded-lg p-0.5">
                  <button
                    type="button"
                    disabled={index === 0}
                    onClick={() => handleMoveOrder(index, 'up')}
                    className="p-1.5 text-[#64748B] hover:text-[#0284C7] disabled:opacity-20 cursor-pointer"
                    title="Geser ke Atas"
                  >
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    disabled={index === slides.length - 1}
                    onClick={() => handleMoveOrder(index, 'down')}
                    className="p-1.5 text-[#64748B] hover:text-[#0284C7] disabled:opacity-20 cursor-pointer"
                    title="Geser ke Bawah"
                  >
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Enable / Disable Toggle */}
                <button
                  type="button"
                  onClick={() => handleToggleEnable(slide.id)}
                  className={`p-2 rounded-lg border text-xs font-semibold flex items-center gap-1 cursor-pointer transition-colors ${
                    slide.enabled
                      ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                      : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                  }`}
                  title={slide.enabled ? 'Nonaktifkan Slide' : 'Aktifkan Slide'}
                >
                  {slide.enabled ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>

                {/* Edit */}
                <button
                  type="button"
                  onClick={() => handleOpenEditModal(slide)}
                  className="p-2 rounded-lg bg-[#F0F7FF] text-[#0284C7] border border-[#E0F2FE] hover:bg-[#E0F2FE] transition-colors cursor-pointer"
                  title="Edit Konten Slide"
                >
                  <Edit2 className="w-4 h-4" />
                </button>

                {/* Delete */}
                <button
                  type="button"
                  onClick={() => handleDeleteSlide(slide.id)}
                  className="p-2 rounded-lg bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100 transition-colors cursor-pointer"
                  title="Hapus Slide"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ---------------------------------------------------- */}
      {/* EDIT / ADD MODAL                                     */}
      {/* ---------------------------------------------------- */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-[#E0F2FE] relative max-h-[90vh] overflow-y-auto space-y-5">
            <div className="flex items-center justify-between border-b border-[#E0F2FE] pb-4">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-xl bg-sky-50 text-[#0284C7]">
                  <Smartphone className="w-5 h-5" />
                </div>
                <h3 className="font-display font-black text-lg text-[#172033] uppercase">
                  {editingSlide ? 'Edit Slide Onboarding' : 'Tambah Slide Baru'}
                </h3>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                className="p-1.5 text-[#64748B] hover:text-[#172033] rounded-lg hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveModalForm} className="space-y-4">
              {uploadError && (
                <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{uploadError}</span>
                </div>
              )}

              {/* Title Field */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-[#172033]">
                  Judul Headline Slide <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  placeholder="Contoh: Nikmati Kopi Spesialti Asli Dumai"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:outline-none focus:border-[#0284C7] focus:ring-2 focus:ring-sky-100 text-sm font-medium"
                />
              </div>

              {/* Description Field */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-[#172033]">
                  Deskripsi / Subtitle
                </label>
                <textarea
                  rows={3}
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Contoh: Biji kopi nusantara yang disangrai dengan presisi untuk menghadirkan aroma dan cita rasa terbaik."
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:outline-none focus:border-[#0284C7] focus:ring-2 focus:ring-sky-100 text-sm font-medium"
                />
              </div>

              {/* Image Upload Area */}
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-[#172033]">
                  Gambar Slide Visual
                </label>

                {formImage ? (
                  <div className="relative rounded-2xl overflow-hidden aspect-[16/9] border border-slate-200 bg-slate-900 group">
                    <img
                      src={resolveMediaUrl(formImage)}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="px-3 py-1.5 bg-white text-[#172033] rounded-lg text-xs font-bold uppercase tracking-wider flex items-center gap-1 cursor-pointer"
                      >
                        <Upload className="w-3.5 h-3.5" />
                        Ganti Foto
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormImage('')}
                        className="px-3 py-1.5 bg-rose-600 text-white rounded-lg text-xs font-bold uppercase tracking-wider flex items-center gap-1 cursor-pointer"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Hapus
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-slate-300 hover:border-[#0284C7] bg-[#F8FBFF] rounded-2xl p-6 text-center cursor-pointer transition-all flex flex-col items-center justify-center gap-2"
                  >
                    <div className="p-3 rounded-full bg-sky-50 text-[#0284C7]">
                      <Upload className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-[#172033]">
                        {isUploading ? 'Mengupload gambar...' : 'Klik untuk upload gambar slide'}
                      </p>
                      <p className="text-[11px] text-[#64748B] mt-0.5">
                        Rekomendasi rasio 4:3 atau 16:9 (Maks. 10MB)
                      </p>
                    </div>
                  </div>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>

              {/* Direct Image URL input fallback */}
              <div className="space-y-1">
                <label className="text-[11px] text-[#64748B]">Atau masukkan URL gambar langsung:</label>
                <input
                  type="text"
                  value={formImage}
                  onChange={(e) => setFormImage(e.target.value)}
                  placeholder="https://..."
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-xs font-mono"
                />
              </div>

              {/* Enable toggle & Order */}
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div className="space-y-1">
                  <label className="text-xs font-bold uppercase tracking-wider text-[#172033]">
                    Urutan Slide
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={formOrder}
                    onChange={(e) => setFormOrder(Number(e.target.value))}
                    className="w-full px-3 py-2 rounded-xl border border-slate-300 text-sm font-bold"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold uppercase tracking-wider text-[#172033]">
                    Status Tampil
                  </label>
                  <button
                    type="button"
                    onClick={() => setFormEnabled(!formEnabled)}
                    className={`w-full py-2 px-3 rounded-xl border text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                      formEnabled
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                        : 'bg-slate-100 text-slate-600 border-slate-300'
                    }`}
                  >
                    {formEnabled ? (
                      <>
                        <Check className="w-4 h-4" />
                        <span>Aktif</span>
                      </>
                    ) : (
                      <>
                        <X className="w-4 h-4" />
                        <span>Nonaktif</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-2.5 pt-4 border-t border-[#E0F2FE]">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2.5 rounded-xl border border-slate-300 text-slate-700 font-bold text-xs uppercase tracking-wider hover:bg-slate-100 transition-all cursor-pointer"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isUploading}
                  className="px-5 py-2.5 rounded-xl bg-[#0284C7] hover:bg-[#0369A1] text-white font-bold text-xs uppercase tracking-wider shadow-sm transition-all cursor-pointer disabled:opacity-50"
                >
                  Simpan Slide
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---------------------------------------------------- */}
      {/* INTERACTIVE LIVE PREVIEW MODAL                       */}
      {/* ---------------------------------------------------- */}
      {showLivePreviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-fadeIn">
          <div className="relative max-w-sm w-full bg-slate-900 rounded-[36px] p-3 shadow-2xl border-4 border-slate-800 ring-1 ring-white/10">
            {/* Phone Notch/Speaker simulation */}
            <div className="absolute top-6 left-1/2 -translate-x-1/2 w-28 h-4 bg-slate-950 rounded-full z-30 flex items-center justify-center">
              <div className="w-3 h-3 rounded-full bg-slate-900 mr-2" />
              <div className="w-8 h-1 bg-slate-800 rounded-full" />
            </div>

            {/* Close Preview Button */}
            <button
              type="button"
              onClick={() => setShowLivePreviewModal(false)}
              className="absolute -top-12 right-0 p-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition-all cursor-pointer"
              title="Tutup Preview"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Interactive Preview Container */}
            <PwaOnboarding
              slides={slides}
              isPreview={true}
              onComplete={() => {
                showToast('Preview selesai: Customer diarahkan ke Homepage.', 'info');
                setShowLivePreviewModal(false);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
